import { useCallback, useEffect, useState } from "react";

import { api } from "@/services/api";

/**
 * The notifications shown under the bell.
 *
 * Polled rather than pushed: these are raised by a background job that runs
 * daily, so a minute of latency is irrelevant, and a socket for it would be
 * machinery with nothing to do.
 */

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  actionUrl?: string;
  createdAt: string;
  readAt: string | null;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function useAppNotifications() {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const response = await api.get("/notifications");
      const data = response.data?.data ?? {};
      setNotifications(data.notifications ?? []);
      setUnread(data.unread ?? 0);
    } catch {
      // A failing notification fetch must never take a page with it. The bell
      // simply shows nothing.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const markRead = useCallback(
    async (id: string) => {
      // Optimistic: the badge should drop the moment it is opened, and the
      // server call only confirms it.
      setNotifications((current) =>
        current.map((n) =>
          n.id === id && !n.readAt
            ? { ...n, readAt: new Date().toISOString() }
            : n,
        ),
      );
      setUnread((n) => Math.max(0, n - 1));
      try {
        await api.post(`/notifications/${id}/read`);
      } catch {
        void refresh();
      }
    },
    [refresh],
  );

  return { notifications, unread, refresh, markRead };
}
